import { useEffect, useMemo, useRef, useState, type ClipboardEvent } from "react";
import { useNavigate } from "react-router";
import { CopyIcon, Maximize2, Minimize2, RefreshCcwIcon, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  PickRefMixedEditor,
  type PickRefMixedEditorHandle,
} from "@/components/brand-site/PickRefMixedEditor";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import {
  Conversation,
  ConversationContent,
  ConversationEmptyState,
  ConversationScrollButton,
} from "@/components/ai-elements/conversation";
import {
  Message,
  MessageAction,
  MessageActions,
  MessageContent,
} from "@/components/ai-elements/message";
import {
  Reasoning,
  ReasoningContent,
  ReasoningTrigger,
} from "@/components/ai-elements/reasoning";
import { Shimmer } from "@/components/ai-elements/shimmer";
import {
  PromptInput,
  PromptInputActionMenu,
  PromptInputActionMenuContent,
  PromptInputActionMenuItem,
  PromptInputActionMenuTrigger,
  PromptInputBody,
  PromptInputFooter,
  PromptInputProvider,
  PromptInputSubmit,
  PromptInputTextarea,
  PromptInputTools,
  usePromptInputController,
  type PromptInputMessage,
} from "@/components/ai-elements/prompt-input";
import { Suggestion, Suggestions } from "@/components/ai-elements/suggestion";
import { SHELL_HEADER_CLASS } from "@/components/shell/shell-header";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { cn } from "@/lib/utils";
import type { HermesMessage } from "@/lib/hermes-api";
import {
  hermesMessageHtmlPreviewsFromPayload,
  hermesMessageProposalsFromPayload,
  hermesMessageScoreOptimizePlansFromPayload,
  isHermesAssistantRetryable,
} from "@/lib/hermes-api";
import { markdownToPlainText } from "@/lib/markdown-to-plain";
import { hermesAssistantParts } from "@/lib/hermes-thinking";
import {
  buildEmptyConversationSuggestions,
  isConversationEmpty,
  type ConversationSuggestion,
  type KbSuggestionSnapshot,
} from "@/lib/hermes-conversation-suggestions";
import { buildKnowledgeEntryChips } from "@/lib/hermes-knowledge-entry";
import { loadKbSuggestionSnapshot } from "@/lib/hermes-kb-suggestion-snapshot";
import { useHermes } from "./HermesProvider";
import { HermesSessionList } from "./HermesSessionList";
import { HermesSessionsMenu } from "./HermesSessionsMenu";
import { BrandSiteStudioSessionsMenu } from "./BrandSiteStudioSessionsMenu";
import { HermesSkillsMenu } from "./HermesSkillsMenu";
import {
  HermesThinkingIndicator,
  softenHermesStreamDetail,
} from "./HermesThinkingIndicator";
import { hermesReasoningShouldStream, hermesReasoningTimerPaused } from "@/lib/hermes-stream-phase";
import type { CapabilityDraft } from "@/lib/hermes-capability-bindings";
import type { HermesQueuedMessage } from "@/lib/hermes-send-queue";
import { HermesCapabilityBar } from "./HermesCapabilityBar";
import { HermesSendQueueList } from "./HermesSendQueueList";
import { parseHermesSources } from "@/lib/hermes-citations";
import { HermesMessageCitations } from "./HermesMessageCitations";
import { HermesHtmlPreviewCard } from "./HermesHtmlPreviewCard";
import { hermesMessageBrandSiteWriteFromPayload } from "@/lib/hermes-brand-site-write";
import { HermesMediaCandidatesGallery } from "./HermesMediaCandidatesGallery";
import { HermesMaterialPreviewCard } from "./HermesMaterialPreviewCard";
import { HermesChoiceCards } from "./HermesChoiceCard";
import { WriteConfirmCard } from "./WriteConfirmCard";
import { BrandSiteScoreOptimizeConfirmCard } from "./BrandSiteScoreOptimizeConfirmCard";
import { hermesMessageChoicesFromPayload } from "@/lib/hermes-choices";
import { hermesMessageMediaCandidatesFromPayload } from "@/lib/hermes-media-candidates";
import { hermesMessageImagePreviewsFromPayload } from "@/lib/hermes-material-preview";
import { Component, type ErrorInfo, type ReactNode } from "react";
import type { JourneyAuthoringContext } from "@/lib/hermes-journey-authoring";
import {
  collapseHtmlFencesForChat,
  type BrandSiteAuthoringContext,
} from "@/lib/hermes-brand-site-authoring";
import type { PlatformTemplateIdeContext } from "@/lib/hermes-platform-template-ide";
import { parsePickEditChatDisplay } from "@/lib/brand-site-preview-nav";
import {
  buildPickEditPromptFromBundle,
  buildPickEditPromptFromComposer,
  mergePickRefs,
  parsePickRefBundleFromText,
  type PickRefBundle,
} from "@/lib/brand-site-pick-refs";
import { segmentsToUserRequest, type PickRefComposerContent } from "@/lib/pick-ref-mixed-editor";
import { resolveHermesStopRestore } from "@/lib/hermes-composer-stop-restore";
import { PickEditCommandCard } from "./PickEditCommandCard";

/** Isolates half-landed Reasoning UI so session switch still renders messages. */
class ReasoningSafeBoundary extends Component<
  { children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[HermesReasoning]", error, info.componentStack);
  }

  render() {
    if (this.state.failed) return null;
    return this.props.children;
  }
}

function hermesThinkingMessage(isStreaming: boolean, duration?: number): ReactNode {
  if (isStreaming || duration === 0) {
    return <Shimmer duration={1}>思考中…</Shimmer>;
  }
  if (duration === undefined) {
    return <p>思考过程</p>;
  }
  return <p>思考了 {duration} 秒</p>;
}

function parseHasSources(message: HermesMessage): boolean {
  return parseHermesSources(message.tool_payload).length > 0;
}

function formatMessageTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("zh-CN", {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function HermesMessageItem({
  message,
  accountId,
  chatId,
  isStreaming = false,
  statusDetail = null,
  streamStatus = null,
  canRetry = false,
  isSending = false,
  collapseHtml = false,
  onRetry,
  onSendChoice,
}: {
  message: HermesMessage;
  accountId: string;
  chatId: number | null;
  isStreaming?: boolean;
  statusDetail?: string | null;
  /** 原始流状态（未 soften），用于冻结思考计时 */
  streamStatus?: string | null;
  canRetry?: boolean;
  isSending?: boolean;
  collapseHtml?: boolean;
  onRetry?: (assistantMessageId: number) => void;
  onSendChoice?: (content: string) => void | Promise<void>;
}) {
  const isUser = message.role === "user";
  const role = isUser ? "user" : "assistant";
  const label = isUser ? "你" : "Hermes";
  const timeLabel = formatMessageTime(message.created_at);
  const writeProposals = isUser
    ? []
    : hermesMessageProposalsFromPayload(message.tool_payload);
  const scoreOptimizePlans = isUser
    ? []
    : hermesMessageScoreOptimizePlansFromPayload(message.tool_payload);
  const htmlPreviews = isUser
    ? []
    : hermesMessageHtmlPreviewsFromPayload(message.tool_payload);
  const imagePreviews = isUser
    ? []
    : hermesMessageImagePreviewsFromPayload(message.tool_payload);
  const mediaCandidates = isUser
    ? null
    : hermesMessageMediaCandidatesFromPayload(message.tool_payload);
  const choiceCards = isUser ? [] : hermesMessageChoicesFromPayload(message.tool_payload);
  const brandSiteWrite = isUser
    ? null
    : hermesMessageBrandSiteWriteFromPayload(message.tool_payload);
  const content = message.content?.trim() ?? "";
  const pickEdit = isUser ? parsePickEditChatDisplay(content) : null;
  const { thinking, answer } = isUser
    ? { thinking: "", answer: content }
    : hermesAssistantParts(message);
  const isReasoningStreaming = hermesReasoningShouldStream(
    isStreaming,
    Boolean(thinking),
    Boolean(answer),
    streamStatus ?? statusDetail,
  );
  const reasoningTimerPaused = hermesReasoningTimerPaused(streamStatus ?? statusDetail);
  const hasSources = !isUser && parseHasSources(message);
  /**
   * 流式忙碌行：
   * - 有真实工具 status 时始终展示（含「已思考、尚无正文、正在跑工具」）
   * - 尚无正文时展示三点/状态
   * - 已有正文/引用但仍在流式（后续工具轮）时继续展示
   */
  const showBusyIndicator =
    isStreaming && (Boolean(statusDetail) || !answer || hasSources);
  const showAwaitingConfirm = !isStreaming && writeProposals.length > 0;
  const showScoreOptimizeConfirm = !isStreaming && chatId !== null && scoreOptimizePlans.length > 0;
  const showHtmlPreviews = !isStreaming && htmlPreviews.length > 0;
  const showImagePreviews = !isStreaming && imagePreviews.length > 0;
  const showMediaCandidates = !isStreaming && Boolean(mediaCandidates?.items.length);
  const showChoices = !isStreaming && choiceCards.length > 0;
  const citationMessage = (() => {
    const withAnswer =
      !isUser && answer !== (message.content ?? "")
        ? { ...message, content: answer }
        : message;
    if (isUser || !collapseHtml) return withAnswer;
    return { ...withAnswer, content: collapseHtmlFencesForChat(withAnswer.content ?? "") };
  })();
  const markdownSource = isUser ? content : answer || content;
  const showRetry = !isUser && canRetry && isHermesAssistantRetryable(message);
  const showActions = Boolean(markdownSource) || showRetry;
  const [copyMenuOpen, setCopyMenuOpen] = useState(false);

  async function writeClipboard(text: string) {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("已复制");
    } catch {
      toast.error("复制失败，请稍后重试");
    }
  }

  async function handleCopyPlain() {
    if (!markdownSource) return;
    const plain = pickEdit
      ? pickEdit.userRequest
      : isUser
        ? markdownSource
        : markdownToPlainText(markdownSource);
    await writeClipboard(plain);
    setCopyMenuOpen(false);
  }

  async function handleCopyMarkdown() {
    if (!markdownSource) return;
    await writeClipboard(markdownSource);
    setCopyMenuOpen(false);
  }

  return (
    <Message from={role} data-role={role} className="gap-1">
      <div
        className={cn(
          "flex items-baseline gap-2 text-[11px] leading-none text-muted-foreground",
          isUser && "justify-end text-right",
        )}
      >
        <span className="font-medium text-foreground/80">{label}</span>
        {timeLabel ? (
          <time dateTime={message.created_at} className="tabular-nums">
            {timeLabel}
          </time>
        ) : null}
      </div>

      <MessageContent
        className={cn(
          isUser
            ? "text-right"
            : "w-full max-w-full overflow-auto",
        )}
      >
        {isUser ? (
          pickEdit ? (
            <PickEditCommandCard parsed={pickEdit} raw={content} />
          ) : (
            <p className="whitespace-pre-wrap break-words leading-relaxed">{content}</p>
          )
        ) : (
          <>
            {thinking ? (
              <ReasoningSafeBoundary key={`reasoning-${message.id}`}>
                <Reasoning
                  className="mb-2 w-full"
                  isStreaming={isReasoningStreaming}
                  timerPaused={reasoningTimerPaused}
                  defaultOpen={isReasoningStreaming}
                >
                  <ReasoningTrigger
                    className="gap-1.5 text-xs [&_svg]:size-3.5"
                    getThinkingMessage={hermesThinkingMessage}
                  />
                  <ReasoningContent className="mt-2 text-xs leading-relaxed">
                    {thinking}
                  </ReasoningContent>
                </Reasoning>
              </ReasoningSafeBoundary>
            ) : null}
            {answer || hasSources ? (
              <HermesMessageCitations
                message={citationMessage}
                isStreaming={isStreaming}
              />
            ) : null}
            {showBusyIndicator ? (
              <HermesThinkingIndicator
                className={thinking || answer || hasSources ? "mt-2" : undefined}
                detail={statusDetail}
              />
            ) : !thinking && !answer && !hasSources ? (
              <p className="text-muted-foreground">…</p>
            ) : null}
          </>
        )}
      </MessageContent>

      {showActions && !isStreaming ? (
        <MessageActions
          className={cn(
            "gap-0.5 transition-opacity",
            isUser && "justify-end self-end",
            showRetry || copyMenuOpen
              ? "opacity-100"
              : "opacity-0 focus-within:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100",
          )}
        >
          {markdownSource ? (
            isUser ? (
              <MessageAction
                tooltip="复制"
                label="复制消息"
                aria-label="复制消息"
                onClick={() => void handleCopyPlain()}
              >
                <CopyIcon className="size-3.5" />
              </MessageAction>
            ) : (
              <HoverCard
                open={copyMenuOpen}
                onOpenChange={setCopyMenuOpen}
                openDelay={80}
                closeDelay={120}
              >
                <HoverCardTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label="复制消息"
                    aria-haspopup="menu"
                    aria-expanded={copyMenuOpen}
                  >
                    <CopyIcon className="size-3.5" />
                    <span className="sr-only">复制消息</span>
                  </Button>
                </HoverCardTrigger>
                <HoverCardContent
                  side="top"
                  align="start"
                  sideOffset={6}
                  className="w-auto min-w-0 rounded-xl p-1"
                >
                  <div className="flex flex-col gap-0.5" role="menu">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      role="menuitem"
                      className="h-8 justify-start px-2.5 text-xs font-medium"
                      onClick={() => void handleCopyPlain()}
                    >
                      复制纯文本
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      role="menuitem"
                      className="h-8 justify-start px-2.5 text-xs font-medium"
                      onClick={() => void handleCopyMarkdown()}
                    >
                      复制 Markdown
                    </Button>
                  </div>
                </HoverCardContent>
              </HoverCard>
            )
          ) : null}
          {showRetry ? (
            <MessageAction
              tooltip="重试"
              label="重新生成回复"
              aria-label="重新生成回复"
              disabled={isSending}
              onClick={() => onRetry?.(message.id)}
            >
              <RefreshCcwIcon className="size-3.5" />
            </MessageAction>
          ) : null}
        </MessageActions>
      ) : null}

      {!isUser && showHtmlPreviews ? (
        <div className="mt-2 w-full space-y-2">
          <p className="text-xs font-medium text-muted-foreground">页面预览</p>
          {htmlPreviews.map((preview) => (
            <HermesHtmlPreviewCard
              key={preview.id}
              accountId={accountId}
              preview={preview}
              relatedPreviews={htmlPreviews}
              brandSiteWrite={brandSiteWrite}
            />
          ))}
        </div>
      ) : null}

      {!isUser && showImagePreviews ? (
        <div className="mt-2 w-full space-y-2">
          <p className="text-xs font-medium text-muted-foreground">素材预览</p>
          {imagePreviews.map((preview) => (
            <HermesMaterialPreviewCard
              key={preview.slug}
              accountId={accountId}
              preview={preview}
            />
          ))}
        </div>
      ) : null}

      {!isUser && showMediaCandidates && mediaCandidates ? (
        <div className="mt-2 w-full space-y-2">
          <p className="text-xs font-medium text-muted-foreground">媒体候选</p>
          <HermesMediaCandidatesGallery accountId={accountId} payload={mediaCandidates} />
        </div>
      ) : null}

      {!isUser && showChoices ? (
        <div className="mt-2 w-full">
          <HermesChoiceCards
            choices={choiceCards}
            disabled={isSending || !onSendChoice}
            onSubmit={async (text) => {
              if (onSendChoice) await onSendChoice(text);
            }}
          />
        </div>
      ) : null}

      {!isUser && showAwaitingConfirm ? (
        <div className="mt-2 w-full space-y-2">
          <p className="text-xs font-medium text-amber-800 dark:text-amber-200/90">
            本轮已结束 — 写入提案
          </p>
          {writeProposals.map((proposal) => (
            <WriteConfirmCard
              key={proposal.id}
              accountId={accountId}
              proposalId={proposal.id}
              action={proposal.action}
              payload={proposal.payload}
              initialStatus={proposal.status}
            />
          ))}
        </div>
      ) : null}

      {!isUser && showScoreOptimizeConfirm && chatId !== null ? (
        <div className="mt-2 w-full space-y-2">
          {scoreOptimizePlans.map((card, index) => (
            <BrandSiteScoreOptimizeConfirmCard
              key={`${message.id}-${index}`}
              accountId={accountId}
              chatId={chatId}
              planId={card.plan_id}
              plan={card.plan}
            />
          ))}
        </div>
      ) : null}
    </Message>
  );
}

function HermesComposer({
  accountId,
  isSending,
  knowledgeEntry,
  skillsDisabled,
  draft,
  setCapabilityDraft,
  journeyAuthoring,
  lastJourneyAuthoring,
  startJourneyAuthoringSession,
  stopJourneyAuthoring,
  brandSiteAuthoring,
  lastBrandSiteAuthoring,
  startBrandSiteAuthoringSession,
  stopBrandSiteAuthoring,
  platformTemplateIde,
  brandSiteStudioActive = false,
  onSubmit,
  onEnqueue,
  onStop,
  queuedMessages,
  onUpdateQueued,
  onRemoveQueued,
  onPullQueued,
  onSendQueuedNow,
}: {
  accountId: string;
  isSending: boolean;
  knowledgeEntry: boolean;
  skillsDisabled: boolean;
  draft: CapabilityDraft;
  setCapabilityDraft: (draft: CapabilityDraft, options?: { persist?: boolean }) => void;
  journeyAuthoring: JourneyAuthoringContext | null;
  lastJourneyAuthoring: JourneyAuthoringContext | null;
  startJourneyAuthoringSession: (ctx: JourneyAuthoringContext) => void | Promise<void>;
  stopJourneyAuthoring: () => void;
  brandSiteAuthoring: BrandSiteAuthoringContext | null;
  lastBrandSiteAuthoring: BrandSiteAuthoringContext | null;
  startBrandSiteAuthoringSession: (ctx: BrandSiteAuthoringContext) => void;
  stopBrandSiteAuthoring: () => void;
  platformTemplateIde: PlatformTemplateIdeContext | null;
  brandSiteStudioActive?: boolean;
  onSubmit: (message: PromptInputMessage) => void | Promise<void>;
  onEnqueue: (text: string) => void;
  onStop: () => string | null;
  queuedMessages: HermesQueuedMessage[];
  onUpdateQueued: (id: string, content: string) => void;
  onRemoveQueued: (id: string) => void;
  onPullQueued: (id: string) => string | null;
  onSendQueuedNow: (id: string) => void;
}) {
  const { textInput } = usePromptInputController();
  const { brandSiteScoreOptimizeActive, composerSeed, clearComposerSeed } = useHermes();
  const mixedEditorRef = useRef<PickRefMixedEditorHandle>(null);
  /** 停止生成时回填混排编辑器（含点选芯片），发送前快照。 */
  const lastSubmittedComposerRef = useRef<PickRefComposerContent | null>(null);
  const [composerPickBundle, setComposerPickBundle] = useState<PickRefBundle | null>(null);
  const [composerContent, setComposerContent] = useState<PickRefComposerContent | null>(null);

  // 可读性优化：打开时把起步话术填进输入框，小白按回车即可。
  useEffect(() => {
    if (!brandSiteScoreOptimizeActive || !composerSeed) return;
    if (!textInput.value.trim()) {
      textInput.setInput(composerSeed.text);
    }
    clearComposerSeed();
  }, [brandSiteScoreOptimizeActive, composerSeed, clearComposerSeed, textInput]);

  const studioCanSend =
    Boolean(composerContent?.userRequest.trim()) ||
    Boolean(composerContent?.picks.length) ||
    Boolean(textInput.value.trim());

  const composerDraft = brandSiteStudioActive ? (studioCanSend ? "ready" : "") : textInput.value.trim();

  function outgoingText(trimmed: string): string {
    if (brandSiteStudioActive) {
      const mixed = mixedEditorRef.current?.getContent();
      if (mixed && mixed.picks.length > 0) {
        return buildPickEditPromptFromComposer(mixed);
      }
      return trimmed;
    }
    if (!composerPickBundle) return trimmed;
    return buildPickEditPromptFromBundle(composerPickBundle, trimmed);
  }

  function clearComposerPicks() {
    setComposerPickBundle(null);
    setComposerContent(null);
    mixedEditorRef.current?.clear();
  }

  function handleStop() {
    const restore = onStop();
    // 有进行中草稿时不覆盖；设计工作室须写回混排编辑器（仅 setInput 不可见）。
    const target = resolveHermesStopRestore({
      restore,
      draftEmpty: !textInput.value.trim(),
      studioActive: Boolean(brandSiteStudioActive),
      lastComposer: lastSubmittedComposerRef.current,
    });
    if (target.kind === "none") return;
    if (target.kind === "textarea") {
      textInput.setInput(target.text);
      return;
    }
    mixedEditorRef.current?.applyContent(target.content);
    setComposerContent(target.content);
    textInput.setInput(
      target.content.userRequest.trim() || segmentsToUserRequest(target.content.segments),
    );
    mixedEditorRef.current?.focus();
  }

  async function handleComposerSubmit(message: PromptInputMessage) {
    const mixed = brandSiteStudioActive ? mixedEditorRef.current?.getContent() : null;
    const trimmed = brandSiteStudioActive
      ? (mixed?.userRequest.trim() || message.text.trim())
      : message.text.trim();
    if (!trimmed && !mixed?.picks.length) return;
    const content = outgoingText(trimmed);
    if (isSending) {
      onEnqueue(content);
      clearComposerPicks();
      if (brandSiteStudioActive) textInput.clear();
      return;
    }
    // 仅真正发出去的那条才快照，供停止后回填；入队消息不覆盖。
    lastSubmittedComposerRef.current = brandSiteStudioActive && mixed ? mixed : null;
    await onSubmit({ ...message, text: content });
    clearComposerPicks();
    if (brandSiteStudioActive) textInput.clear();
  }

  function handleComposerPaste(event: ClipboardEvent<HTMLTextAreaElement>) {
    const text = event.clipboardData.getData("text/plain");
    const parsed = parsePickRefBundleFromText(text);
    if (!parsed) return;
    event.preventDefault();
    setComposerPickBundle((current) => {
      if (!current) return parsed.bundle;
      if (current.pageSlug !== parsed.bundle.pageSlug) return parsed.bundle;
      return { ...current, picks: mergePickRefs(current.picks, parsed.bundle.picks) };
    });
    if (parsed.remainder) {
      const current = textInput.value.trim();
      textInput.setInput(current ? `${current}\n${parsed.remainder}` : parsed.remainder);
    }
  }

  function handlePull(id: string) {
    const content = onPullQueued(id);
    if (!content) return;
    const current = textInput.value.trim();
    textInput.setInput(current ? `${current}\n\n${content}` : content);
  }

  return (
    <>
      <HermesSendQueueList
        items={queuedMessages}
        onUpdate={onUpdateQueued}
        onRemove={onRemoveQueued}
        onPull={handlePull}
        onSendNow={(id) => void onSendQueuedNow(id)}
      />
      <PromptInput
        className="[&_[data-slot=input-group]]:rounded-xl [&_[data-slot=input-group]]:bg-background [&_[data-slot=input-group]]:shadow-sm [&_[data-slot=input-group]]:has-[data-pick-composer-input]:h-auto [&_[data-slot=input-group]]:has-[data-pick-composer-input]:rounded-2xl"
        onSubmit={handleComposerSubmit}
      >
        <PromptInputBody>
          {brandSiteStudioActive ? (
            <PickRefMixedEditor
              ref={mixedEditorRef}
              variant="input-group"
              disabled={!accountId}
              pageContext={{
                pageSlug: composerContent?.pageSlug ?? "",
                pageTitle: composerContent?.pageTitle ?? null,
                pagePath: composerContent?.pagePath ?? "",
              }}
              className="min-h-12 max-h-[7.5rem]"
              placeholder={
                knowledgeEntry
                  ? "描述企业要点，或粘贴选区…"
                  : isSending
                    ? "输入下一条，Enter 加入队列…"
                    : "点选左侧元素后粘贴，或描述要如何改…"
              }
              onChange={(content) => {
                setComposerContent(content);
                const draft =
                  content?.userRequest.trim() ||
                  (content?.picks.length ? segmentsToUserRequest(content.segments) : "");
                textInput.setInput(draft);
              }}
              onSubmit={() => {
                const form = mixedEditorRef.current
                  ? document.activeElement?.closest("form")
                  : null;
                (form as HTMLFormElement | null)?.requestSubmit();
              }}
            />
          ) : (
            <>
              {composerPickBundle ? (
                <div className="flex flex-wrap gap-1.5 border-b border-border bg-muted/30 px-3 py-2">
                  {composerPickBundle.picks.map((pick) => (
                    <span
                      key={pick.id}
                      className="inline-flex max-w-full items-center gap-0.5 rounded-md border border-primary/50 bg-primary px-1.5 py-0.5 text-[11px] font-medium text-primary-foreground shadow-sm"
                    >
                      {pick.tag} {pick.text.slice(0, 20)}
                    </span>
                  ))}
                </div>
              ) : null}
              <PromptInputTextarea
                disabled={!accountId}
                className="min-h-10 max-h-[7.5rem]"
                onPaste={handleComposerPaste}
                placeholder={
                  knowledgeEntry
                    ? "描述企业要点，或粘贴素材…"
                    : composerPickBundle
                      ? "描述要如何改这些选区…"
                      : isSending
                        ? "输入下一条，Enter 加入队列…"
                        : "输入消息…"
                }
              />
            </>
          )}
        </PromptInputBody>
        <PromptInputFooter>
          <PromptInputTools className="min-w-0 flex-1">
            <PromptInputActionMenu>
              <PromptInputActionMenuTrigger tooltip="添加附件（即将支持）" disabled />
              <PromptInputActionMenuContent>
                <PromptInputActionMenuItem disabled>附件上传（即将支持）</PromptInputActionMenuItem>
              </PromptInputActionMenuContent>
            </PromptInputActionMenu>
            <HermesCapabilityBar
              accountId={accountId}
              draft={draft}
              onDraftChange={skillsDisabled ? undefined : setCapabilityDraft}
              journeyAuthoring={journeyAuthoring}
              lastJourneyAuthoring={lastJourneyAuthoring}
              onStartJourneyAuthoring={startJourneyAuthoringSession}
              onStopJourneyAuthoring={stopJourneyAuthoring}
              brandSiteAuthoring={brandSiteAuthoring}
              lastBrandSiteAuthoring={lastBrandSiteAuthoring}
              onStartBrandSiteAuthoring={startBrandSiteAuthoringSession}
              onStopBrandSiteAuthoring={stopBrandSiteAuthoring}
              platformTemplateIde={platformTemplateIde}
              brandSiteStudioActive={brandSiteStudioActive}
              disabled={skillsDisabled}
            />
          </PromptInputTools>
          <PromptInputSubmit
            disabled={!accountId || (!isSending && !composerDraft)}
            status={isSending ? "streaming" : undefined}
            onStop={handleStop}
            variant="default"
          />
        </PromptInputFooter>
      </PromptInput>
    </>
  );
}

/** Hermes 对话面板；开合/全宽浮层由 WorkspaceChrome 控制；embedded 供 Journey 等内嵌右栏。 */
export function HermesDrawer({
  variant = "dock",
}: {
  variant?: "dock" | "embedded";
}) {
  const {
    open,
    setOpen,
    expanded,
    setExpanded,
    accountId,
    chatId,
    draftSkills,
    capabilityDraft,
    setCapabilityDraft,
    messages,
    isSending,
    isSwitchingSession,
    streamStatus,
    sendMessage,
    sendOrEnqueue,
    enqueueMessage,
    queuedMessages,
    updateQueuedMessage,
    removeQueuedMessage,
    sendQueuedNow,
    retryMessage,
    stopGeneration,
    knowledgeEntry,
    composerFocusNonce,
    journeyAuthoring,
    lastJourneyAuthoring,
    startJourneyAuthoringSession,
    stopJourneyAuthoring,
    brandSiteAuthoring,
    lastBrandSiteAuthoring,
    startBrandSiteAuthoringSession,
    stopBrandSiteAuthoring,
    brandSiteStudioActive,
    brandSiteScoreOptimizeActive,
    platformTemplateIde,
  } = useHermes();
  const navigate = useNavigate();
  const embedded = variant === "embedded";
  const active = embedded || open;

  const composerAreaRef = useRef<HTMLDivElement>(null);
  const activeSkills = draftSkills;
  const hasKbSkill = activeSkills.includes("kb");
  const skillsDisabled = isSending || isSwitchingSession;
  const conversationEmpty = isConversationEmpty(messages);
  const [kbSnapshot, setKbSnapshot] = useState<KbSuggestionSnapshot | null>(null);

  // Semi-automatic KB suggestions: load enterprise fill / inbox when kb skill + empty chat.
  useEffect(() => {
    if (!active || !accountId || knowledgeEntry) return;
    if (!conversationEmpty || !hasKbSkill) return;

    let cancelled = false;
    void loadKbSuggestionSnapshot(accountId).then((snapshot) => {
      if (!cancelled) setKbSnapshot(snapshot);
    });
    return () => {
      cancelled = true;
    };
  }, [active, accountId, knowledgeEntry, conversationEmpty, hasKbSkill]);

  const chips = useMemo((): ConversationSuggestion[] => {
    if (knowledgeEntry) return buildKnowledgeEntryChips(knowledgeEntry);
    if (!conversationEmpty || isSending || activeSkills.length === 0) return [];
    return buildEmptyConversationSuggestions({
      skills: activeSkills,
      kbSnapshot,
    });
  }, [knowledgeEntry, conversationEmpty, isSending, activeSkills, kbSnapshot]);

  const lastMessage = messages[messages.length - 1];
  const streamingAssistantId =
    isSending && lastMessage?.role === "assistant" ? lastMessage.id : null;
  const showTypingPlaceholder =
    isSending && (messages.length === 0 || lastMessage?.role === "user");
  const showEmptyState =
    Boolean(accountId) && conversationEmpty && !isSending && !isSwitchingSession;
  const lastMessageId = lastMessage?.id ?? null;
  const emptyTitle = knowledgeEntry
    ? "开始录入知识"
    : brandSiteScoreOptimizeActive
      ? "开始优化可读性"
      : activeSkills.length > 0
        ? "试试这些起步问题"
        : "开始对话";
  const emptyDescription = knowledgeEntry
    ? "选择下方快捷问题，或直接描述企业要点、粘贴素材。"
    : brandSiteScoreOptimizeActive
      ? "下面已帮你写好要说的话，直接按回车即可；确认计划前不会改页面。"
      : activeSkills.length > 0
        ? "建议根据当前知识库状态生成；点击即可发送。"
        : "在下方选择技能并输入消息。";

  function focusComposerInput() {
    if (!accountId) return;

    // rAF + setTimeout(0)：等状态提交 / Popover 关闭归还焦点后再抢回输入框
    requestAnimationFrame(() => {
      window.setTimeout(() => {
        const textarea = composerAreaRef.current?.querySelector<HTMLTextAreaElement>(
          'textarea[name="message"]',
        );
        if (textarea?.disabled) return;
        textarea?.focus({ preventScroll: true });
      }, 0);
    });
  }

  useEffect(() => {
    if (!active) return;
    focusComposerInput();
  }, [active]);

  useEffect(() => {
    if (!active || composerFocusNonce === 0) return;
    focusComposerInput();
  }, [composerFocusNonce, active]);

  async function handleSubmit({ text }: PromptInputMessage) {
    const trimmed = text.trim();
    if (!trimmed || !accountId) return;
    if (isSwitchingSession) {
      throw new Error("Hermes is busy");
    }
    await sendOrEnqueue(trimmed);
  }

  async function handleChipClick(chip: (typeof chips)[number]) {
    if (chip.href) {
      setOpen(false);
      navigate(chip.href);
      return;
    }
    if (!chip.prompt || !accountId || isSending || isSwitchingSession) return;
    // Chip prompts are always user turns (never seeded as assistant).
    await sendMessage(chip.prompt);
  }

  return (
    <aside
      className="flex h-full min-h-0 w-full flex-col bg-background"
      aria-label="Hermes 助手"
      aria-hidden={!active}
    >
      <header
        className={cn(
          SHELL_HEADER_CLASS,
          "justify-between gap-2 border-border px-4",
        )}
      >
        <div className="min-w-0">
          <h2 className="text-base font-medium leading-none">Hermes</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            {platformTemplateIde
              ? "模板 IDE · 固定场景会话"
              : brandSiteStudioActive
                ? "官网设计 · 场景会话"
                : brandSiteScoreOptimizeActive
                  ? "固定场景会话"
                : knowledgeEntry
                  ? "知识录入助手"
                  : "平台 AI 助手"}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {brandSiteStudioActive ? <BrandSiteStudioSessionsMenu /> : null}
          {!platformTemplateIde && !brandSiteStudioActive && !brandSiteScoreOptimizeActive ? (
            <HermesSkillsMenu />
          ) : null}
          {/* 抽屉态：会话列表；场景会话禁止切换 */}
          {!expanded &&
            !platformTemplateIde &&
            !brandSiteStudioActive &&
            !brandSiteScoreOptimizeActive ? (
            <HermesSessionsMenu />
          ) : null}
          {!embedded ? (
            <>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className="size-7"
                aria-label={expanded ? "恢复窗口" : "全宽展开"}
                aria-pressed={expanded}
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? (
                  <Minimize2 className="size-4" />
                ) : (
                  <Maximize2 className="size-4" />
                )}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className="size-7"
                aria-label="关闭 Hermes"
                onClick={() => setOpen(false)}
              >
                <X className="size-4" />
              </Button>
            </>
          ) : null}
        </div>
      </header>

      {(() => {
        const mainPane = (
          <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col">
            <Conversation className="min-h-0 flex-1 overflow-hidden">
              <ConversationContent className="min-h-full gap-2.5 px-4 py-3">
                {/* 切会话：加载态只画在右侧主面板，左侧列表保持不动 */}
                {isSwitchingSession ? (
                  <div
                    className="flex min-h-48 flex-1 items-center justify-center"
                    aria-busy="true"
                    aria-label="正在加载会话"
                  >
                    <Shimmer duration={1} className="text-sm">
                      加载会话…
                    </Shimmer>
                  </div>
                ) : !accountId ? (
                  <ConversationEmptyState
                    title="尚未进入工作区"
                    description="请先进入账户工作区后再开始对话。"
                  />
                ) : showEmptyState ? (
                  <ConversationEmptyState
                    title={emptyTitle}
                    description={emptyDescription}
                  />
                ) : (
                  messages
                    .filter((message) => message.role !== "system")
                    .map((message) => (
                      <HermesMessageItem
                        key={message.id}
                        message={message}
                        accountId={accountId}
                        chatId={chatId}
                        isStreaming={message.id === streamingAssistantId}
                        statusDetail={
                          message.id === streamingAssistantId
                            ? softenHermesStreamDetail(streamStatus)
                            : null
                        }
                        streamStatus={
                          message.id === streamingAssistantId ? streamStatus : null
                        }
                        canRetry={message.id === lastMessageId}
                        isSending={isSending}
                        collapseHtml={Boolean(brandSiteAuthoring || platformTemplateIde)}
                        onRetry={(id) => void retryMessage(id)}
                        onSendChoice={(text) => sendMessage(text)}
                      />
                    ))
                )}
                {!isSwitchingSession && showTypingPlaceholder ? (
                  <Message from="assistant" data-role="assistant" className="gap-1">
                    <div className="flex items-baseline gap-2 text-[11px] leading-none text-muted-foreground">
                      <span className="font-medium text-foreground/80">Hermes</span>
                    </div>
                    <MessageContent>
                      <HermesThinkingIndicator
                        detail={softenHermesStreamDetail(streamStatus)}
                      />
                    </MessageContent>
                  </Message>
                ) : null}
              </ConversationContent>
              <ConversationScrollButton />
            </Conversation>

            <div ref={composerAreaRef} className="shrink-0 border-t border-border p-3">
              {chips.length > 0 ? (
                <Suggestions className="mb-3 px-0.5">
                  {chips.map((chip) => (
                    <Suggestion
                      key={chip.id}
                      suggestion={chip.label}
                      className="rounded-md"
                      disabled={!accountId || isSending || isSwitchingSession}
                      onClick={() => void handleChipClick(chip)}
                    />
                  ))}
                </Suggestions>
              ) : null}
              <PromptInputProvider>
                <HermesComposer
                  accountId={accountId}
                  isSending={isSending}
                  knowledgeEntry={Boolean(knowledgeEntry)}
                  skillsDisabled={skillsDisabled}
                  draft={capabilityDraft}
                  setCapabilityDraft={setCapabilityDraft}
                  journeyAuthoring={journeyAuthoring}
                  lastJourneyAuthoring={lastJourneyAuthoring}
                  startJourneyAuthoringSession={startJourneyAuthoringSession}
                  stopJourneyAuthoring={stopJourneyAuthoring}
                  brandSiteAuthoring={brandSiteAuthoring}
                  lastBrandSiteAuthoring={lastBrandSiteAuthoring}
                  startBrandSiteAuthoringSession={startBrandSiteAuthoringSession}
                  stopBrandSiteAuthoring={stopBrandSiteAuthoring}
                  platformTemplateIde={platformTemplateIde}
                  brandSiteStudioActive={brandSiteStudioActive}
                  onSubmit={handleSubmit}
                  onEnqueue={enqueueMessage}
                  queuedMessages={queuedMessages}
                  onUpdateQueued={updateQueuedMessage}
                  onRemoveQueued={(id) => {
                    removeQueuedMessage(id);
                  }}
                  onPullQueued={(id) => removeQueuedMessage(id)?.content ?? null}
                  onSendQueuedNow={(id) => void sendQueuedNow(id)}
                  onStop={() => {
                    const restore = stopGeneration();
                    return restore;
                  }}
                />
              </PromptInputProvider>
            </div>
          </div>
        );

        if (!expanded || brandSiteScoreOptimizeActive) {
          return <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{mainPane}</div>;
        }

        return (
          <ResizablePanelGroup
            orientation="horizontal"
            className="min-h-0 flex-1 overflow-hidden"
          >
            <ResizablePanel
              defaultSize={260}
              minSize={200}
              maxSize={420}
              groupResizeBehavior="preserve-pixel-size"
              className="min-h-0"
            >
              <div
                className="flex h-full min-h-0 flex-col bg-muted/20"
                aria-label="Hermes 会话侧栏"
              >
                <HermesSessionList enabled={expanded} variant="sidebar" />
              </div>
            </ResizablePanel>
            <ResizableHandle withHandle />
            <ResizablePanel minSize={320} className="min-h-0">
              {mainPane}
            </ResizablePanel>
          </ResizablePanelGroup>
        );
      })()}
    </aside>
  );
}
